import CaptureFooter from "@/components/lp/CaptureFooter";
import { trackToolStep, trackToolResult, trackToolLead, trackToolCta, enableToolInputTracking } from "@/lib/formTelemetry";
import { trackEvent } from "@/lib/analytics";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "@/lib/router-compat";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import {
  Info, Loader2, Store, Building2, Calculator as CalcIcon, ArrowRight,
  MapPin, Tag, Calendar, Ruler, Euro, Save, Sparkles,
  Shirt, Footprints, UtensilsCrossed, Wine, Coffee, Croissant,
  Scissors, Sparkle, Pill, Stethoscope, ShoppingCart, Apple, Sofa, Gem,
  type LucideIcon,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { useLanguage } from "@/i18n/LanguageContext";
import AuthModal from "@/components/AuthModal";
import DiagnosisAuthFlow from "@/components/DiagnosisAuthFlow";
import RequestSessionModal from "@/components/RequestSessionModal";
import { setPostAuthIntent } from "@/lib/post-auth-intent";
import {
  computeDiagnosis,
  computeOwnerDiagnosis,
  semaforoColor,
  PENDING_DIAGNOSIS_KEY,
  pushGuestDiagnosis,
  type DiagnosisInput,
  type DiagnosisResult,
  type RetailRole,
  type Sector,
  type ZoneRef,
  type TenantLegalFormId,
  type TenantExperienceId,
} from "@/lib/location-diagnosis";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { consumeLiteToolUse } from "@/lib/liteToolGate";
import { AddressAutocomplete, type SelectedAddress } from "@/components/diagnosticador/AddressAutocomplete";
import { LocationMap } from "@/components/diagnosticador/LocationMap";
import { StreetView } from "@/components/diagnosticador/StreetView";
import Header from "@/components/Header";
import DynamicSimulator from "@/components/diagnosticador/DynamicSimulator";

// ── Detailed retail sectors (from MicroDiagnóstico) mapped to the 5 broad sectors
// understood by the diagnosis engine (location-diagnosis.ts).
interface DetailedSector {
  id: string;
  esLabel: string;
  enLabel: string;
  Icon: LucideIcon;
  mapsTo: Sector;
}
const DETAILED_SECTORS: DetailedSector[] = [
  { id: "tienda_ropa", esLabel: "Tienda de Ropa", enLabel: "Clothing Store", Icon: Shirt, mapsTo: "Moda" },
  { id: "zapateria", esLabel: "Zapatería", enLabel: "Shoe Store", Icon: Footprints, mapsTo: "Moda" },
  { id: "joyeria", esLabel: "Joyería", enLabel: "Jewelry", Icon: Gem, mapsTo: "Moda" },
  { id: "restaurante", esLabel: "Restaurante", enLabel: "Restaurant", Icon: UtensilsCrossed, mapsTo: "Hostelería" },
  { id: "bar", esLabel: "Bar", enLabel: "Bar", Icon: Wine, mapsTo: "Hostelería" },
  { id: "cafeteria", esLabel: "Cafetería", enLabel: "Café", Icon: Coffee, mapsTo: "Hostelería" },
  { id: "panaderia", esLabel: "Panadería", enLabel: "Bakery", Icon: Croissant, mapsTo: "Alimentación" },
  { id: "supermercado", esLabel: "Supermercado", enLabel: "Supermarket", Icon: ShoppingCart, mapsTo: "Alimentación" },
  { id: "alimentacion", esLabel: "Tienda de Alimentación", enLabel: "Grocery Store", Icon: Apple, mapsTo: "Alimentación" },
  { id: "peluqueria", esLabel: "Peluquería", enLabel: "Hair Salon", Icon: Scissors, mapsTo: "Servicios" },
  { id: "estetica", esLabel: "Centro de Estética", enLabel: "Beauty Center", Icon: Sparkle, mapsTo: "Cosmética" },
  { id: "farmacia", esLabel: "Farmacia", enLabel: "Pharmacy", Icon: Pill, mapsTo: "Servicios" },
  { id: "parafarmacia", esLabel: "Parafarmacia", enLabel: "Parapharmacy", Icon: Stethoscope, mapsTo: "Cosmética" },
  { id: "muebles", esLabel: "Muebles y Decoración", enLabel: "Furniture & Decor", Icon: Sofa, mapsTo: "Servicios" },
  { id: "inmobiliaria", esLabel: "Inmobiliaria", enLabel: "Real Estate", Icon: Building2, mapsTo: "Servicios" },
  { id: "otro", esLabel: "Otro", enLabel: "Other", Icon: Store, mapsTo: "Servicios" },
];

const ROUTE_PATH = "/diagnostico-locales-comerciales";

// ── Owner-only solvency profile options ──
const TENANT_LEGAL_FORMS = [
  { id: "autonomo", esLabel: "Autónomo", enLabel: "Self-employed" },
  { id: "sl", esLabel: "SL (Sociedad Limitada)", enLabel: "SL (Limited Company)" },
  { id: "sa", esLabel: "SA (Sociedad Anónima)", enLabel: "SA (Public Limited Co.)" },
  { id: "extranjera", esLabel: "Empresa Extranjera", enLabel: "Foreign Company" },
] as const;
type TenantLegalForm = typeof TENANT_LEGAL_FORMS[number]["id"];

const TENANT_EXPERIENCES = [
  { id: "primera", esLabel: "Es su primera tienda", enLabel: "First store" },
  { id: "1_2", esLabel: "Tiene 1-2 tiendas activas", enLabel: "1-2 active stores" },
  { id: "cadena", esLabel: "Cadena consolidada (+3 tiendas)", enLabel: "Established chain (+3 stores)" },
] as const;
type TenantExperience = typeof TENANT_EXPERIENCES[number]["id"];

const labelLegal = (id: string | null, lang: "es" | "en") =>
  TENANT_LEGAL_FORMS.find((o) => o.id === id)?.[lang === "es" ? "esLabel" : "enLabel"] ?? "";
const labelExperience = (id: string | null, lang: "es" | "en") =>
  TENANT_EXPERIENCES.find((o) => o.id === id)?.[lang === "es" ? "esLabel" : "enLabel"] ?? "";

const inputSchema = z
  .object({
    role: z.enum(["inquilino", "propietario"]),
    zipCode: z.string().regex(/^\d{5}$/),
    requestedRent: z.number().positive(),
    sqm: z.number().positive(),
    averageTicket: z.number().positive(),
    diasApertura: z.number().int().min(1).max(31),
    tenantLegalForm: z.string().nullable().optional(),
    tenantExperience: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.role === "propietario") {
      if (data.tenantLegalForm && !TENANT_LEGAL_FORMS.some((o) => o.id === data.tenantLegalForm)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["tenantLegalForm"], message: "invalid" });
      }
      if (data.tenantExperience && !TENANT_EXPERIENCES.some((o) => o.id === data.tenantExperience)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["tenantExperience"], message: "invalid" });
      }
    }
  });

const fmtEuro = (n: number) =>
  n.toLocaleString("es-ES", { maximumFractionDigits: 0 }) + " €";
const fmtNum = (n: number, d = 0) =>
  n.toLocaleString("es-ES", { maximumFractionDigits: d });

const getSessionId = (): string => {
  const k = "loc_diag_session";
  let s = localStorage.getItem(k);
  if (!s) {
    s = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(k, s);
  }
  return s;
};

interface DiagnosticadorLocalesProps {
  /** "public" = landing with SEO + paywall; "private" = dashboard tool with save button. */
  mode?: "public" | "private";
  /** When true, omit Header/Footer/Hero/SEO and run in compact inline mode (always private). */
  embedded?: boolean;
  /** Public experience but without its own Header/Footer/SEO (used inside the panel home). */
  hideChrome?: boolean;
  /** Fired after a successful save in embedded mode so the parent can refresh and collapse. */
  onSaved?: () => void;
  /** Rol elegido (inquilino/propietario); se avisa al montar y en cada cambio. */
  onRoleChange?: (role: "inquilino" | "propietario") => void;
}




const fingerprintAddress = (formatted: string, lat: number | null, lng: number | null) => {
  const base = formatted.trim().toLowerCase().replace(/\s+/g, " ");
  if (lat != null && lng != null) return `${base}|${lat.toFixed(5)},${lng.toFixed(5)}`;
  return base;
};

const extractStreet = (formatted: string) => formatted.split(",")[0]?.trim() ?? formatted;

const DiagnosticadorLocales = ({ mode = "public", embedded = false, hideChrome = false, onSaved, onRoleChange }: DiagnosticadorLocalesProps) => {
  const isPrivate = mode === "private" || embedded;
  // "bare" = sin cabecera/pie propios (embebido en otra página)
  const bare = embedded || hideChrome;
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hydrateId = embedded ? null : searchParams.get("id");
  const { isAuthenticated, user } = useAuthState();


  // Sync language with URL (SEO localized routing)
  useEffect(() => {
    if (isPrivate) return;
    const path = window.location.pathname;
    if (path.startsWith("/en/") && lang !== "en") setLang("en");
    else if (path === "/diagnostico-locales-comerciales" && lang !== "es") setLang("es");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user toggles language while on this landing, redirect to the matching URL.
  useEffect(() => {
    if (isPrivate) return;
    const path = window.location.pathname;
    const onEs = path === "/diagnostico-locales-comerciales";
    const onEn = path === "/en/retail-location-analysis" || path === "/retail-location-analysis";
    if (lang === "en" && onEs) navigate("/en/retail-location-analysis", { replace: true });
    else if (lang === "es" && onEn) navigate("/diagnostico-locales-comerciales", { replace: true });
  }, [lang, isPrivate, navigate]);


  const [role, setRole] = useState<RetailRole>("inquilino");
  // El rol solo se comunica cuando la persona cambia el selector o introduce su
  // primer dato (mismo momento que tool_step_started paso 1); nunca al montar.
  const roleAnnounced = useRef(false);
  const announceRole = (r: RetailRole) => {
    roleAnnounced.current = true;
    if (r === "inquilino" || r === "propietario") onRoleChange?.(r);
  };
  const [address, setAddress] = useState<SelectedAddress | null>(null);
  const [zipCode, setZipCode] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [placesAvailable, setPlacesAvailable] = useState(true);
  const [rent, setRent] = useState("");
  const [sqm, setSqm] = useState("");
  const [detailedSectorId, setDetailedSectorId] = useState<string>(DETAILED_SECTORS[0]!.id);
  const sector: Sector = useMemo(
    () => DETAILED_SECTORS.find((s) => s.id === detailedSectorId)?.mapsTo ?? "Moda",
    [detailedSectorId],
  );
  const [ticket, setTicket] = useState("");
  const [dias, setDias] = useState("26");
  const [empleados, setEmpleados] = useState("1");
  const [candidates, _setCandidates] = useState("");
  const [tenantLegalForm, setTenantLegalForm] = useState<TenantLegalFormId | "">("");
  const [tenantExperience, setTenantExperience] = useState<TenantExperienceId | "">("");

  useEffect(() => { if (!isPrivate) enableToolInputTracking(); }, [isPrivate]);
  const [loadingStep, setLoadingStep] = useState(0); // 0 idle, 1/2/3 messages
  const [result, setResult] = useState<DiagnosisResult | null>(null);
  const [resultInput, setResultInput] = useState<DiagnosisInput | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [proLimitOpen, setProLimitOpen] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  // Hydration from ?id= (private mode only)
  useEffect(() => {
    if (!isPrivate || !hydrateId || !isAuthenticated || !user?.id) return;
    let cancelled = false;
    (async () => {
      setHydrating(true);
      const { data, error } = await supabase
        .from("saved_locations")
        .select("*")
        .eq("id", hydrateId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setHydrating(false);
        return;
      }
      setRole((data.role as RetailRole) ?? "inquilino");
      setZipCode(data.zip_code ?? "");
      setManualAddress(data.formatted_address ?? "");
      if (data.lat != null && data.lng != null) {
        setAddress({
          formattedAddress: data.formatted_address ?? "",
          zipCode: data.zip_code ?? "",
          lat: Number(data.lat),
          lng: Number(data.lng),
        });
      }
      setRent(String(data.requested_rent ?? ""));
      setSqm(String(data.sqm ?? ""));
      setDetailedSectorId(data.detailed_sector_id || DETAILED_SECTORS[0]!.id);
      setTicket(data.average_ticket != null ? String(data.average_ticket) : "");
      setDias(String(data.dias_apertura ?? "26"));
      setEmpleados(String((data.raw_input as any)?.empleadosEstimados ?? 1));
      setTenantLegalForm(((data as any).tenant_legal_form ?? "") as TenantLegalForm | "");
      setTenantExperience(((data as any).tenant_experience ?? "") as TenantExperience | "");

      // Recompute synchronously (no Hollywood delay) for instant view
      const { data: zoneRow } = await supabase
        .from("macro_zone_data")
        .select("zip_code,neighborhood_name,avg_rent_sqm,sector_viability")
        .eq("zip_code", data.zip_code ?? "")
        .maybeSingle();
      const zone: ZoneRef | null = zoneRow
        ? {
            zip_code: zoneRow.zip_code,
            neighborhood_name: zoneRow.neighborhood_name,
            avg_rent_sqm: Number(zoneRow.avg_rent_sqm),
            sector_viability: (zoneRow.sector_viability as Record<string, boolean>) ?? {},
          }
        : null;
      const sectorMapped: Sector =
        DETAILED_SECTORS.find((s) => s.id === data.detailed_sector_id)?.mapsTo ?? "Moda";
      const input: DiagnosisInput = {
        role: (data.role as RetailRole) ?? "inquilino",
        zipCode: data.zip_code ?? "",
        requestedRent: Number(data.requested_rent ?? 0),
        sqm: Number(data.sqm ?? 0),
        sector: sectorMapped,
        detailedSectorId: data.detailed_sector_id || undefined,
        averageTicket: Number(data.average_ticket ?? 1),
        diasApertura: Number(data.dias_apertura ?? 26),
        empleadosEstimados: Math.max(1, Number((data as any).empleados_estimados ?? (data.raw_input as any)?.empleadosEstimados ?? 1)),
      };
      const res =
        input.role === "propietario"
          ? computeOwnerDiagnosis(
              {
                zipCode: input.zipCode,
                requestedRent: input.requestedRent,
                sqm: input.sqm,
                sector: input.sector,
                detailedSectorId: input.detailedSectorId,
                tenantLegalForm: (((data as any).tenant_legal_form ?? null) as TenantLegalFormId | null),
                tenantExperience: (((data as any).tenant_experience ?? null) as TenantExperienceId | null),
              },
              zone,
            )
          : computeDiagnosis(input, zone);
      setResult(res);
      setResultInput(input);
      setCurrentLocationId(data.id);
      setHydrating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isPrivate, hydrateId, isAuthenticated, user?.id]);

  // Reset state if we navigate to private route without ?id (e.g. "+ Nuevo diagnóstico")
  useEffect(() => {
    if (!isPrivate || hydrateId) return;
    // only reset if we were holding a previous result
    if (result || resultInput) {
      setResult(null);
      setResultInput(null);
      setCurrentLocationId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate, hydrateId]);

  // Restore pending diagnosis after auth
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    try {
      const raw = localStorage.getItem(PENDING_DIAGNOSIS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.result || !parsed?.input) {
        localStorage.removeItem(PENDING_DIAGNOSIS_KEY);
        return;
      }
      setResult(parsed.result);
      setResultInput(parsed.input);

      const cleanup = () => localStorage.removeItem(PENDING_DIAGNOSIS_KEY);

      void (async () => {
        try {
          trackToolLead("diagnostico_locales", "registro", parsed.input?.role ?? null);
          // 1. Claim the anonymous lead row to this user
          if (parsed.sessionId) {
            await supabase.rpc("claim_evaluation_leads_by_session", {
              p_session_id: parsed.sessionId,
            });
          }

          // 2. Persist into the user's portfolio so it shows in "Mis Locales"
          const inp = parsed.input as DiagnosisInput;
          const res = parsed.result as DiagnosisResult;
          const formattedAddress: string = parsed.formattedAddress || "";
          const lat: number | null = parsed.lat ?? null;
          const lng: number | null = parsed.lng ?? null;
          const fingerprint = fingerprintAddress(formattedAddress, lat, lng);
          const payload = {
            user_id: user.id,
            place_id: null as string | null,
            address_fingerprint: fingerprint,
            formatted_address: formattedAddress,
            street_name: extractStreet(formattedAddress),
            city: "",
            zip_code: inp.zipCode,
            lat,
            lng,
            role: inp.role,
            business_sector: inp.sector,
            detailed_sector_id: parsed.detailedSectorId ?? inp.detailedSectorId ?? null,
            requested_rent: inp.requestedRent,
            sqm: inp.sqm,
            average_ticket: inp.role === "inquilino" ? inp.averageTicket : null,
            dias_apertura: inp.diasApertura,
            semaforo_result: res.semaforo,
            precio_m2_usuario: res.precioM2Usuario,
            precio_m2_zona: res.precioM2Zona,
            facturacion_limite: res.facturacionLimite,
            facturacion_optima: res.facturacionOptima,
            unidades_limite: res.unidadesLimite,
            unidades_optimas: res.unidadesOptimas,
            tenant_legal_form: inp.role === "propietario" ? (parsed.tenantLegalForm || null) : null,
            tenant_experience: inp.role === "propietario" ? (parsed.tenantExperience || null) : null,
            raw_input: {
              ...inp,
              candidates: parsed.candidates,
              tenantLegalForm: parsed.tenantLegalForm || null,
              tenantExperience: parsed.tenantExperience || null,
            } as any,
          } as any;
          const { data: upserted } = await supabase
            .from("saved_locations")
            .upsert(payload, { onConflict: "user_id,address_fingerprint" })
            .select("id")
            .maybeSingle();
          if (upserted?.id) setCurrentLocationId(upserted.id);
        } catch (e) {
          console.error("[restorePendingDiagnosis]", e);
        } finally {
          cleanup();
        }
      })();
    } catch {/* ignore */}
  }, [isAuthenticated, user?.id]);

  const handleSubmit = async () => {
    if (!isPrivate) trackToolStep("diagnostico_locales", 2, "calcular", role);
    let normalizedZip = zipCode.trim();
    if (!/^\d{5}$/.test(normalizedZip)) {
      const haystack = `${address?.formattedAddress ?? ""} ${manualAddress}`;
      const match = haystack.match(/\b(\d{5})\b/);
      if (match) {
        normalizedZip = match[1]!;
        setZipCode(normalizedZip);
      }
    }
    if (!normalizedZip || (!address && !manualAddress.trim())) {
      toast.error(t("locationDiagnosis", "addressRequired"));
      return;
    }
    const parsed = inputSchema.safeParse({
      role,
      zipCode: normalizedZip,
      requestedRent: Number(rent),
      sqm: Number(sqm),
      averageTicket: role === "inquilino" ? Number(ticket) : 1,
      diasApertura: Number(dias),
      tenantLegalForm: role === "propietario" ? (tenantLegalForm || null) : null,
      tenantExperience: role === "propietario" ? (tenantExperience || null) : null,
    });
    if (!parsed.success) {
      toast.error(t("locationDiagnosis", "invalidZip"));
      return;
    }

    if (!(await consumeLiteToolUse("premises", lang === "en" ? "en" : "es"))) return;

    // ── Hollywood Loading Sequence (4×1s = 4s total) ──
    const zonePromise = supabase
      .from("macro_zone_data")
      .select("zip_code,neighborhood_name,avg_rent_sqm,sector_viability")
      .eq("zip_code", parsed.data.zipCode)
      .maybeSingle();

    setLoadingStep(1);
    await new Promise((r) => setTimeout(r, 1000));
    setLoadingStep(2);
    await new Promise((r) => setTimeout(r, 1000));
    setLoadingStep(3);
    await new Promise((r) => setTimeout(r, 1000));
    setLoadingStep(4);
    await new Promise((r) => setTimeout(r, 1000));

    const { data: zoneRow } = await zonePromise;
    const zone: ZoneRef | null = zoneRow
      ? {
          zip_code: zoneRow.zip_code,
          neighborhood_name: zoneRow.neighborhood_name,
          avg_rent_sqm: Number(zoneRow.avg_rent_sqm),
          sector_viability: (zoneRow.sector_viability as Record<string, boolean>) ?? {},
        }
      : null;

    const input: DiagnosisInput = {
      role,
      zipCode: parsed.data.zipCode,
      requestedRent: parsed.data.requestedRent,
      sqm: parsed.data.sqm,
      sector,
      detailedSectorId,
      averageTicket: parsed.data.averageTicket,
      diasApertura: parsed.data.diasApertura,
      empleadosEstimados: role === "inquilino" ? Math.max(1, Number(empleados) || 1) : 1,
    };
    const res =
      role === "propietario"
        ? computeOwnerDiagnosis(
            {
              zipCode: input.zipCode,
              requestedRent: input.requestedRent,
              sqm: input.sqm,
              sector: input.sector,
              detailedSectorId: input.detailedSectorId,
              tenantLegalForm: (tenantLegalForm || null) as TenantLegalFormId | null,
              tenantExperience: (tenantExperience || null) as TenantExperienceId | null,
            },
            zone,
          )
        : computeDiagnosis(input, zone);


    if (!isPrivate) trackToolResult("diagnostico_locales", role);
    // PUBLIC mode only: persist analytical lead + paywall flow.
    // PRIVATE mode: no auto-save; user must explicitly press "Save".
    const sessionId = getSessionId();
    const formattedAddressForLead = address?.formattedAddress || manualAddress || "";
    const latForLead = address?.lat ?? null;
    const lngForLead = address?.lng ?? null;
    if (!isPrivate) {
      supabase
        .from("evaluation_leads")
        .insert({
          user_id: user?.id ?? null,
          session_id: sessionId,
          role,
          zip_code: input.zipCode,
          requested_rent: input.requestedRent,
          sqm: input.sqm,
          business_sector: input.sector,
          average_ticket: role === "inquilino" ? input.averageTicket : null,
          dias_apertura: input.diasApertura,
          semaforo_result: res.semaforo,
          ventas_diarias_necesarias: res.unidadesLimite,
          facturacion_minima: res.facturacionLimite,
          precio_m2_usuario: res.precioM2Usuario,
          precio_m2_zona: res.precioM2Zona,
          tenant_legal_form: role === "propietario" ? (tenantLegalForm || null) : null,
          tenant_experience: role === "propietario" ? (tenantExperience || null) : null,
          raw_input: {
            ...input,
            candidates: candidates ? Number(candidates) : undefined,
            tenantLegalForm: role === "propietario" ? (tenantLegalForm || null) : null,
            tenantExperience: role === "propietario" ? (tenantExperience || null) : null,
            // Address details — required to rebuild saved_locations after cross-host signup.
            formattedAddress: formattedAddressForLead,
            lat: latForLead,
            lng: lngForLead,
            detailedSectorId,
          },
        } as any)
        .then(() => {/* fire & forget */});

      // Stash for paywall → restore after register
      if (!isAuthenticated) {
        try {
          const guestEntry = {
            input,
            result: res,
            sessionId,
            savedAt: new Date().toISOString(),
            detailedSectorId,
            candidates: candidates ? Number(candidates) : undefined,
            tenantLegalForm: tenantLegalForm || null,
            tenantExperience: tenantExperience || null,
            formattedAddress: formattedAddressForLead,
            lat: latForLead,
            lng: lngForLead,
          };
          localStorage.setItem(PENDING_DIAGNOSIS_KEY, JSON.stringify(guestEntry));
          pushGuestDiagnosis(guestEntry);
        } catch {/* ignore */}
      }
    }

    setResult(res);
    setResultInput(input);
    setCurrentLocationId(null); // new calculation → unsaved
    setLoadingStep(0);

    // ── Auto-save to user portfolio when authenticated (any mode) ──
    // Replaces the legacy "💾 Save in panel" button: persistence is now invisible.
    if (isAuthenticated && user?.id) {
      void (async () => {
        try {
          const formattedAddress = address?.formattedAddress || manualAddress || "";
          const lat = address?.lat ?? null;
          const lng = address?.lng ?? null;
          const fingerprint = fingerprintAddress(formattedAddress, lat, lng);
          const payload = {
            user_id: user.id,
            place_id: null as string | null,
            address_fingerprint: fingerprint,
            formatted_address: formattedAddress,
            street_name: extractStreet(formattedAddress),
            city: "",
            zip_code: input.zipCode,
            lat,
            lng,
            role: input.role,
            business_sector: input.sector,
            detailed_sector_id: detailedSectorId,
            requested_rent: input.requestedRent,
            sqm: input.sqm,
            average_ticket: input.role === "inquilino" ? input.averageTicket : null,
            dias_apertura: input.diasApertura,
            semaforo_result: res.semaforo,
            precio_m2_usuario: res.precioM2Usuario,
            precio_m2_zona: res.precioM2Zona,
            facturacion_limite: res.facturacionLimite,
            facturacion_optima: res.facturacionOptima,
            unidades_limite: res.unidadesLimite,
            unidades_optimas: res.unidadesOptimas,
            tenant_legal_form: input.role === "propietario" ? (tenantLegalForm || null) : null,
            tenant_experience: input.role === "propietario" ? (tenantExperience || null) : null,
            raw_input: {
              ...input,
              candidates: candidates ? Number(candidates) : undefined,
              tenantLegalForm: input.role === "propietario" ? (tenantLegalForm || null) : null,
              tenantExperience: input.role === "propietario" ? (tenantExperience || null) : null,
            } as any,
          } as any;
          const { data: upserted } = await supabase
            .from("saved_locations")
            .upsert(payload, { onConflict: "user_id,address_fingerprint" })
            .select("id")
            .maybeSingle();
          if (upserted?.id) setCurrentLocationId(upserted.id);
          if (embedded && onSaved) onSaved();
        } catch (e) {
          console.error("[autoSaveLocation]", e);
        }
      })();
    }

    // Freemium paywall: only in public mode for guests.
    if (!isPrivate && !isAuthenticated) {
      setPostAuthIntent({ kind: "path", path: "/dashboard" }); // Redirect to dashboard after signup
      setTimeout(() => setAuthOpen(true), 400);
    }
  };

  const openSignup = () => {
    trackToolCta("diagnostico_locales", "registro", undefined, role);
    setPostAuthIntent({ kind: "path", path: "/dashboard" }); // Redirect to dashboard after signup
    setAuthOpen(true);
  };

  // Save current diagnosis to the user portfolio (private mode only).
  const saveLocation = async () => {
    if (!isAuthenticated || !user?.id) {
      toast.error(t("locationDiagnosis", "saveLoginRequired"));
      return;
    }
    if (!result || !resultInput) {
      toast.error(t("locationDiagnosis", "saveRequiresCalc"));
      return;
    }
    setSaving(true);
    try {
      // Freemium gate: count existing locations if user has no active panel.
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      const isAdmin = !!roles?.some((r: any) => r.role === "admin");

      let hasUnlimited = isAdmin;
      if (!hasUnlimited) {
        const { data: mc } = await supabase
          .from("module_content")
          .select("module_key")
          .eq("access_type", "included");
        const includedKeys = new Set((mc ?? []).map((m: any) => m.module_key));
        if (includedKeys.size > 0) {
          const { data: subs } = await supabase
            .from("user_module_subscriptions")
            .select("module_key, status")
            .eq("user_id", user.id)
            .eq("status", "active");
          hasUnlimited = !!(subs ?? []).some((s: any) => includedKeys.has(s.module_key));
        }
      }

      if (!hasUnlimited) {
        const { count } = await supabase
          .from("saved_locations")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id);
        const existing = count ?? 0;
        // Allow update of the currently-loaded one even if at limit
        if (existing >= 3 && !currentLocationId) {
          setProLimitOpen(true);
          setSaving(false);
          return;
        }
      }

      const formattedAddress = address?.formattedAddress || manualAddress || "";
      const lat = address?.lat ?? null;
      const lng = address?.lng ?? null;
      const fingerprint = fingerprintAddress(formattedAddress, lat, lng);

      const payload = {
        user_id: user.id,
        place_id: null as string | null, // SelectedAddress doesn't expose place_id yet
        address_fingerprint: fingerprint,
        formatted_address: formattedAddress,
        street_name: extractStreet(formattedAddress),
        city: "",
        zip_code: resultInput.zipCode,
        lat,
        lng,
        role: resultInput.role,
        business_sector: resultInput.sector,
        detailed_sector_id: detailedSectorId,
        requested_rent: resultInput.requestedRent,
        sqm: resultInput.sqm,
        average_ticket: resultInput.role === "inquilino" ? resultInput.averageTicket : null,
        dias_apertura: resultInput.diasApertura,
        semaforo_result: result.semaforo,
        precio_m2_usuario: result.precioM2Usuario,
        precio_m2_zona: result.precioM2Zona,
        facturacion_limite: result.facturacionLimite,
        facturacion_optima: result.facturacionOptima,
        unidades_limite: result.unidadesLimite,
        unidades_optimas: result.unidadesOptimas,
        tenant_legal_form: resultInput.role === "propietario" ? (tenantLegalForm || null) : null,
        tenant_experience: resultInput.role === "propietario" ? (tenantExperience || null) : null,
        raw_input: {
          ...resultInput,
          candidates: candidates ? Number(candidates) : undefined,
          tenantLegalForm: resultInput.role === "propietario" ? (tenantLegalForm || null) : null,
          tenantExperience: resultInput.role === "propietario" ? (tenantExperience || null) : null,
        } as any,
      } as any;

      const isUpdate = !!currentLocationId;
      // Upsert by (user_id, address_fingerprint) when place_id is null
      const { data: upserted, error } = await supabase
        .from("saved_locations")
        .upsert(payload, { onConflict: "user_id,address_fingerprint" })
        .select("id")
        .maybeSingle();

      if (error) throw error;
      if (upserted?.id) {
        setCurrentLocationId(upserted.id);
        // reflect in URL so the user can share / bookmark (not in embedded mode)
        if (!embedded && (!hydrateId || hydrateId !== upserted.id)) {
          setSearchParams({ id: upserted.id }, { replace: true });
        }
      }
      toast.success(t("locationDiagnosis", isUpdate ? "updatedToast" : "savedToast"));
      if (embedded && onSaved) onSaved();
    } catch (e) {
      console.error("[saveLocation]", e);
      toast.error(t("locationDiagnosis", "saveFailedToast"));

    } finally {
      setSaving(false);
    }
  };

  // ── Render ──
  if (result && resultInput) {
    return (
      <ResultsView
        result={result}
        input={resultInput}
        tenantLegalForm={tenantLegalForm || null}
        tenantExperience={tenantExperience || null}
        locked={!isPrivate && !isAuthenticated}
        isPrivate={isPrivate}
        embedded={embedded}
        hideChrome={hideChrome}

        canSave={isPrivate && isAuthenticated}
        saving={saving}
        isSaved={!!currentLocationId}
        onSave={saveLocation}
        onSignupClick={openSignup}
        onBookSession={() => setBookingOpen(true)}
        onResetClick={() => {
          setResult(null);
          setResultInput(null);
          setCurrentLocationId(null);
          if (isPrivate && hydrateId) setSearchParams({}, { replace: true });
        }}
      >
        {!isPrivate && (
          <DiagnosisAuthFlow
            open={authOpen}
            onOpenChange={setAuthOpen}
            sessionId={getSessionId()}
          />
        )}
        <RequestSessionModal open={bookingOpen} onOpenChange={setBookingOpen} />
        {/* Freemium portfolio-limit modal (private only) */}
        {isPrivate && proLimitOpen && (
          <FreemiumLimitModal
            open={proLimitOpen}
            onOpenChange={setProLimitOpen}
            onCta={() => {
              setProLimitOpen(false);
              navigate(lang === "es" ? "/herramientas/calculadora" : "/en/tools/calculator");
            }}
          />
        )}
      </ResultsView>
    );
  }

  const es = lang === "es";
  const diagPathEs = "/diagnostico-locales-comerciales";
  const diagPathEn = "/en/retail-location-analysis";
  const diagUrl = `https://retaillemon.com${es ? diagPathEs : diagPathEn}`;
  const altUrl = `https://retaillemon.com${es ? diagPathEn : diagPathEs}`;
  const seoTitle = t("seo", "diagTitle");
  const seoDesc = t("seo", "diagDesc");
  const seoKeywords = t("seo", "diagKeywords");
  const seoImageAlt = t("seo", "diagImageAlt");
  const ogImage = "https://retaillemon.com/og-image.jpg";

  const diagJsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: es ? "Diagnóstico de Locales Comerciales" : "Retail Location Analysis",
      url: diagUrl,
      description: seoDesc,
      keywords: seoKeywords,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      provider: { "@type": "Organization", name: "Retail Lemon", url: "https://retaillemon.com" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Retail Lemon", item: "https://retaillemon.com" },
        { "@type": "ListItem", position: 2, name: es ? "Diagnóstico de Locales" : "Location Analysis", item: diagUrl },
      ],
    },
  ];

  return (
    <div {...(isPrivate ? {} : { "data-rl-tool": "diagnostico_locales", "data-rl-variant": role })} onInputCapture={() => { if (!roleAnnounced.current) announceRole(role); }} className={bare ? "" : "min-h-screen bg-background flex flex-col"}>

      {!isPrivate && !hideChrome && (
        <Helmet>
          <html lang={lang} />
          <title>{seoTitle}</title>
          <meta name="description" content={seoDesc} />
          <meta name="keywords" content={seoKeywords} />
          <link rel="canonical" href={diagUrl} />
          <link rel="alternate" hrefLang={es ? "en" : "es"} href={altUrl} />
          <link rel="alternate" hrefLang={es ? "es" : "en"} href={diagUrl} />
          <link rel="alternate" hrefLang="x-default" href={`https://retaillemon.com${diagPathEs}`} />
          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Retail Lemon" />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDesc} />
          <meta property="og:url" content={diagUrl} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:alt" content={seoImageAlt} />
          <meta property="og:locale" content={es ? "es_ES" : "en_US"} />
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDesc} />
          <meta name="twitter:image" content={ogImage} />
          <meta name="twitter:image:alt" content={seoImageAlt} />
          {diagJsonLd.map((schema, i) => (
            <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
          ))}
        </Helmet>
      )}
      {!bare && <Header />}
      <main className={bare ? "" : "flex-1 pt-3 pb-24"}>



      <div className={`container mx-auto px-4 ${bare ? "max-w-5xl" : "max-w-7xl"}`}>
        {hydrating && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("locationDiagnosis", "loading1")}</span>
          </div>
        )}
        {/* Hero — only on the public landing (inside the panel TabInfoHeader owns it) */}
        {!isPrivate && !hideChrome && (
        <div className="text-center mb-6">
          <Badge variant="outline" className="mb-4 border-yellow-300 bg-yellow-50 text-yellow-900">
            Pre-firma · Retail Intelligence
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
            {t("locationDiagnosis", "controlCenterTitle")}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {t("locationDiagnosis", "heroSubtitle")}
          </p>
          <p className="text-xs text-muted-foreground/80 mt-3">
            {t("locationDiagnosis", "socialProof")}
          </p>
          <div className="mt-4 flex items-start gap-2 max-w-2xl mx-auto rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <span>
              {t("locationDiagnosis", "differentiator")}{" "}
              <button
                onClick={() => navigate(lang === "es" ? "/herramientas/calculadora" : "/en/tools/calculator")}
                className="text-yellow-700 underline font-medium hover:text-yellow-800"
              >
                <CalcIcon className="inline h-3 w-3 mr-0.5" strokeWidth={1.5} />
                {lang === "es" ? "Calculadora de Rentabilidad →" : "Profitability Calculator →"}
              </button>
            </span>
          </div>
        </div>
        )}

        {/* Split layout: form (left) + reality panel (right) */}
        <div className={`grid grid-cols-1 gap-5 items-stretch ${bare ? "md:grid-cols-[1fr_1.25fr]" : "md:grid-cols-[1fr_1.5fr]"}`}>
          {/* ─── LEFT: Form ─── */}
          <div data-tour="locations-diagnosis-form">
            <Tabs value={role} onValueChange={(v) => { setRole(v as RetailRole); announceRole(v as RetailRole); }}>
              {!embedded && (
              <TabsList className="grid w-full grid-cols-2 h-auto gap-1 rounded-lg p-1">
                <TabsTrigger
                  value="inquilino"
                  title={t("locationDiagnosis", "tabInquilino")}
                  aria-label={t("locationDiagnosis", "tabInquilino")}
                  className="min-h-11 min-w-0 px-2 text-center text-xs leading-tight sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Store className="h-4 w-4 mr-1.5 shrink-0" strokeWidth={1.5} />
                  <span className="min-w-0 truncate">{t("locationDiagnosis", "tabInquilinoShort")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="propietario"
                  title={t("locationDiagnosis", "tabPropietario")}
                  aria-label={t("locationDiagnosis", "tabPropietario")}
                  className="min-h-11 min-w-0 px-2 text-center text-xs leading-tight sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Building2 className="h-4 w-4 mr-1.5 shrink-0" strokeWidth={1.5} />
                  <span className="min-w-0 truncate">{t("locationDiagnosis", "tabPropietarioShort")}</span>
                </TabsTrigger>
              </TabsList>
              )}

              <TabsContent value="inquilino" className="mt-3">
                <FormCard>
                  <AddressField
                    address={address}
                    label={t("locationDiagnosis", "addressLabel")}
                    placeholder={t("locationDiagnosis", "addressPlaceholder")}
                    onSelect={(a) => {
                      const zip = /^\d{5}$/.test(a.zipCode)
                        ? a.zipCode
                        : (a.formattedAddress.match(/\b(\d{5})\b/)?.[1] ?? "");
                      setAddress({ ...a, zipCode: zip });
                      setZipCode(zip);
                      setManualAddress(a.formattedAddress);
                    }}
                    onManualChange={(value) => {
                      setManualAddress(value);
                      setAddress(null);
                    }}
                    zipCode={zipCode}
                    onZipCodeChange={setZipCode}
                    placesAvailable={placesAvailable}
                    onAvailabilityChange={setPlacesAvailable}
                    unavailableMessage={t("locationDiagnosis", "placesUnavailable")}
                    zipPlaceholder={t("locationDiagnosis", "zipCode")}
                  />
                  <FormRow label={t("locationDiagnosis", "requestedRent")} icon={Euro}>
                    <Input className="h-9" inputMode="decimal" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="2500" />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "sqm")} icon={Ruler}>
                    <Input className="h-9" inputMode="decimal" value={sqm} onChange={(e) => setSqm(e.target.value)} placeholder="80" />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "sector")} icon={Store} full>
                    <DetailedSectorSelect value={detailedSectorId} onChange={setDetailedSectorId} lang={lang} />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "averageTicket")} icon={Tag}>
                    <Input className="h-9" inputMode="decimal" value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="45" />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "daysOpen")} icon={Calendar}>
                    <Input className="h-9" inputMode="numeric" value={dias} onChange={(e) => setDias(e.target.value)} placeholder="26" />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "employees")} icon={Users} full>
                    <Input className="h-9 sm:max-w-[calc(50%-0.5rem)]" inputMode="numeric" value={empleados} onChange={(e) => setEmpleados(e.target.value)} placeholder="1" />
                  </FormRow>
                </FormCard>
              </TabsContent>

              <TabsContent value="propietario" className="mt-3">
                <FormCard>
                  <AddressField
                    address={address}
                    label={t("locationDiagnosis", "addressLabel")}
                    placeholder={t("locationDiagnosis", "addressPlaceholder")}
                    onSelect={(a) => {
                      const zip = /^\d{5}$/.test(a.zipCode)
                        ? a.zipCode
                        : (a.formattedAddress.match(/\b(\d{5})\b/)?.[1] ?? "");
                      setAddress({ ...a, zipCode: zip });
                      setZipCode(zip);
                      setManualAddress(a.formattedAddress);
                    }}
                    onManualChange={(value) => {
                      setManualAddress(value);
                      setAddress(null);
                    }}
                    zipCode={zipCode}
                    onZipCodeChange={setZipCode}
                    placesAvailable={placesAvailable}
                    onAvailabilityChange={setPlacesAvailable}
                    unavailableMessage={t("locationDiagnosis", "placesUnavailable")}
                    zipPlaceholder={t("locationDiagnosis", "zipCode")}
                  />
                  <FormRow label={t("locationDiagnosis", "requestedRent")} icon={Euro}>
                    <Input className="h-9" inputMode="decimal" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="2500" />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "sqm")} icon={Ruler}>
                    <Input className="h-9" inputMode="decimal" value={sqm} onChange={(e) => setSqm(e.target.value)} placeholder="80" />
                  </FormRow>
                  <FormRow label={t("locationDiagnosis", "candidateSector")} icon={Store} full>
                    <DetailedSectorSelect value={detailedSectorId} onChange={setDetailedSectorId} lang={lang} />
                  </FormRow>
                  <FormRow
                    label={lang === "es" ? "Forma jurídica del inquilino" : "Tenant legal form"}
                    icon={Building2}
                  >
                    <Select value={tenantLegalForm} onValueChange={(v) => setTenantLegalForm(v as TenantLegalForm)}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={lang === "es" ? "Selecciona forma jurídica" : "Select legal form"} />
                      </SelectTrigger>
                      <SelectContent>
                        {TENANT_LEGAL_FORMS.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {lang === "es" ? o.esLabel : o.enLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormRow>
                  <FormRow
                    label={lang === "es" ? "Experiencia comercial" : "Commercial experience"}
                    icon={Store}
                  >
                    <Select value={tenantExperience} onValueChange={(v) => setTenantExperience(v as TenantExperience)}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={lang === "es" ? "Selecciona experiencia" : "Select experience"} />
                      </SelectTrigger>
                      <SelectContent>
                        {TENANT_EXPERIENCES.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {lang === "es" ? o.esLabel : o.enLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormRow>
                </FormCard>
              </TabsContent>
            </Tabs>

            <div className="mt-2 flex flex-col gap-2">
            <label
              htmlFor="legal-accept"
              className="flex items-start gap-2 cursor-pointer select-none"
            >
              <input
                id="legal-accept"
                type="checkbox"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-yellow-500 cursor-pointer"
              />
              <span className="text-[11px] leading-snug text-gray-500">
                {t("locationDiagnosis", "legalAcceptText")}
              </span>
            </label>

            <Button
              onClick={handleSubmit}
              disabled={loadingStep > 0 || !legalAccepted}
              className="h-10 w-full text-sm font-semibold bg-yellow-400 hover:bg-yellow-500 text-black transition-all disabled:opacity-50"
            >
              {loadingStep === 0 ? (
                t("locationDiagnosis", "calculate")
              ) : (
                <span className="inline-flex items-center gap-3 w-full justify-center">
                  <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                  <span className="text-sm">
                    {loadingStep === 1 && t("locationDiagnosis", "loading1")}
                    {loadingStep === 2 && t("locationDiagnosis", "loading2")}
                    {loadingStep === 3 && t("locationDiagnosis", "loading3")}
                    {loadingStep === 4 && t("locationDiagnosis", "loading4")}
                  </span>
                  <span className="hidden sm:inline-flex gap-1 ml-1">
                    {[1, 2, 3, 4].map((s) => (
                      <span
                        key={s}
                        className={`inline-block h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                          s <= loadingStep ? "bg-black/80" : "bg-black/20"
                        }`}
                      />
                    ))}
                  </span>
                </span>
              )}
            </Button>
            </div>
          </div>

          {/* ─── RIGHT: Reality panel (Map + Street View) ─── */}
          <RealityPanel
            address={address}
            loading={loadingStep > 0}
            t={t}
          />
        </div>
        </div>
      </main>
      {!isPrivate && !hideChrome && <CaptureFooter slug="diagnostico" pathEs="/diagnostico-locales-comerciales" pathEn="/en/tools/my-premises" shareText={t("shareLanding", "diagText")} />}
      {/* Private-mode modals also need to mount in form view */}
      {isPrivate && proLimitOpen && (
        <FreemiumLimitModal
          open={proLimitOpen}
          onOpenChange={setProLimitOpen}
          onCta={() => {
            setProLimitOpen(false);
            navigate(lang === "es" ? "/herramientas/calculadora" : "/en/tools/calculator");
          }}
        />
      )}
    </div>
  );
};

const FormCard = ({ children }: { children: React.ReactNode }) => (
  <Card className="grid grid-cols-1 gap-x-4 gap-y-2.5 p-4 sm:grid-cols-2">{children}</Card>
);

const FormRow = ({
  label,
  icon: Icon,
  children,
  full = false,
}: {
  label: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  /** Span both columns of the compact form grid. */
  full?: boolean;
}) => (
  <div className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-[11px] uppercase leading-tight tracking-wide text-muted-foreground inline-flex min-h-8 items-end gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 text-foreground" strokeWidth={1.75} />}
      {label}
    </Label>
    {children}
  </div>
);

const AddressField = ({
  address,
  label,
  placeholder,
  onSelect,
  onManualChange,
  zipCode,
  onZipCodeChange,
  placesAvailable,
  onAvailabilityChange,
  unavailableMessage,
  zipPlaceholder,
}: {
  address: SelectedAddress | null;
  label: string;
  placeholder: string;
  onSelect: (a: SelectedAddress) => void;
  onManualChange: (value: string) => void;
  zipCode: string;
  onZipCodeChange: (value: string) => void;
  placesAvailable: boolean;
  onAvailabilityChange: (available: boolean) => void;
  unavailableMessage: string;
  zipPlaceholder: string;
}) => (
  <div className="space-y-1 sm:col-span-2">
    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1.5">
      <MapPin className="h-3.5 w-3.5 text-foreground" strokeWidth={1.75} /> {label}
    </Label>
    <AddressAutocomplete
      onSelect={onSelect}
      placeholder={placeholder}
      initialValue={address?.formattedAddress ?? ""}
      onManualChange={onManualChange}
      onAvailabilityChange={onAvailabilityChange}
    />
    {!placesAvailable && (
      <p className="text-xs text-muted-foreground">{unavailableMessage}</p>
    )}
    {/* ZIP code is extracted silently from Google Places — no visible input. */}
  </div>
);

const DetailedSectorSelect = ({
  value,
  onChange,
  lang,
}: {
  value: string;
  onChange: (id: string) => void;
  lang: "es" | "en";
}) => {
  const current = DETAILED_SECTORS.find((s) => s.id === value);
  const CurrentIcon = current?.Icon ?? Store;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9">
        <SelectValue>
          <span className="inline-flex items-center gap-2">
            <CurrentIcon className="h-4 w-4 text-foreground" strokeWidth={1.75} />
            {current ? (lang === "es" ? current.esLabel : current.enLabel) : ""}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {DETAILED_SECTORS.map((s) => {
          const Icon = s.Icon;
          return (
            <SelectItem key={s.id} value={s.id}>
              <span className="inline-flex items-center gap-2">
                <Icon className="h-4 w-4 text-foreground" strokeWidth={1.75} />
                {lang === "es" ? s.esLabel : s.enLabel}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

// ── Right column: dynamic Map + Street View "reality" cards ──
const RealityPanel = ({
  address,
  loading,
  t,
}: {
  address: SelectedAddress | null;
  loading: boolean;
  t: (group: string, key: string) => string;
}) => {
  if (!address) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/30 h-full min-h-[280px] p-8 md:p-12 flex flex-col items-center justify-center text-center animate-fade-in">
        <MapPin className="h-10 w-10 text-foreground mb-3" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground max-w-xs">
          {t("locationDiagnosis", "rightColPlaceholder")}
        </p>
      </div>
    );
  }
  return (
    <div className={`space-y-4 transition-all duration-500 ${loading ? "blur-[6px] pointer-events-none" : ""}`}>
      <Card
        key={`map-${address.lat}-${address.lng}`}
        className="overflow-hidden shadow-md border-border/60 animate-fade-in"
      >
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-foreground" strokeWidth={1.75} />
            {t("locationDiagnosis", "mapCardTitle")}
          </p>
          <p className="text-xs text-foreground/70 mt-0.5 line-clamp-1">{address.formattedAddress}</p>
        </div>
        <div className="p-3 pt-1">
          <LocationMap lat={address.lat} lng={address.lng} zoom={17} />
        </div>
      </Card>

      <Card
        key={`sv-${address.lat}-${address.lng}`}
        className="overflow-hidden shadow-md border-border/60 animate-fade-in"
        style={{ animationDelay: "120ms" }}
      >
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1.5">
            <Store className="h-3.5 w-3.5 text-foreground" strokeWidth={1.75} />
            {t("locationDiagnosis", "streetViewCardTitle")}
          </p>
        </div>
        <div className="p-3 pt-1">
          <StreetView lat={address.lat} lng={address.lng} />
        </div>
      </Card>

      {loading && (
        <p className="text-center text-xs text-muted-foreground animate-pulse">
          {t("locationDiagnosis", "processingReality")}
        </p>
      )}
    </div>
  );
};

// ── Results view ──

interface ResultsViewProps {
  result: DiagnosisResult;
  input: DiagnosisInput;
  tenantLegalForm?: string | null;
  tenantExperience?: string | null;
  locked: boolean;
  isPrivate?: boolean;
  embedded?: boolean;
  hideChrome?: boolean;
  canSave?: boolean;
  saving?: boolean;
  isSaved?: boolean;
  onSave?: () => void;
  onSignupClick: () => void;
  onBookSession: () => void;
  onResetClick: () => void;
  children?: React.ReactNode;
}

const ResultsView = ({
  result, input, tenantLegalForm = null, tenantExperience = null,
  locked, isPrivate = false, embedded = false, hideChrome = false, canSave = false, saving = false, isSaved = false,
  onSave, onSignupClick, onBookSession, onResetClick, children,
}: ResultsViewProps) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const isTenant = input.role === "inquilino";

  return (
    <div className={embedded || hideChrome ? "" : "min-h-screen bg-background flex flex-col"}>
      {!(embedded || hideChrome) && <Header />}
      <main className={embedded || hideChrome ? "" : "flex-1 pt-6 pb-32"}>

        <div className="container max-w-4xl mx-auto px-4">
        <button
          onClick={onResetClick}
          className="text-xs text-muted-foreground hover:text-foreground mb-4"
        >
          ← {t("locationDiagnosis", "historyNew")}
        </button>

        {/* Location header */}
        <Card className="p-5 mb-5 flex items-center gap-3">
          <MapPin className="h-5 w-5 text-foreground shrink-0" strokeWidth={1.75} />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              CP {input.zipCode}
              {result.neighborhoodName && ` · ${result.neighborhoodName}`}
            </p>
            {!result.zoneFound && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("locationDiagnosis", "zoneNotFound")}
              </p>
            )}
          </div>
        </Card>

        {/* TENANT: interactive dynamic simulator (banner + 4 sliders + breakdown) */}
        {isTenant && !locked && (
          <DynamicSimulator input={input} result={result} />
        )}

        {/* TENANT locked preview (blurred placeholder) */}
        {isTenant && locked && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            {t("locationDiagnosis", "paywallSubtitle")}
          </Card>
        )}

        {/* OWNER: keep score card (no simulator) */}
        {!isTenant && (
          <div className={locked ? "pointer-events-none select-none" : ""}>
            <Card className={`p-6 bg-yellow-50 border-yellow-300 ${locked ? "blur-[6px]" : ""}`}>
              <p className="text-xs uppercase tracking-wide text-yellow-900 mb-2">
                {t("locationDiagnosis", "ownerScoreTitle")}
              </p>
              <p className="text-4xl font-bold text-yellow-900">
                {fmtNum(result.ownerScore ?? 0, 0)}
                <span className="text-xl text-yellow-900/70"> / 100</span>
              </p>
              <p className="text-xs text-yellow-900/80 mt-1">
                {t("locationDiagnosis", "ownerScoreSub")}
              </p>
              {(tenantLegalForm || tenantExperience) && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-yellow-900/70 mr-1">
                    {lang === "es" ? "Perfil evaluado:" : "Profile evaluated:"}
                  </span>
                  {tenantLegalForm && (
                    <Badge variant="outline" className="text-[10px] border-yellow-400 bg-white text-yellow-900">
                      {labelLegal(tenantLegalForm, lang)}
                    </Badge>
                  )}
                  {tenantExperience && (
                    <Badge variant="outline" className="text-[10px] border-yellow-400 bg-white text-yellow-900">
                      {labelExperience(tenantExperience, lang)}
                    </Badge>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}





        {/* CTAs cascade (only visible after unlock) */}
        {!locked && (
          <div className="mt-8 space-y-4">
            {/* Auto-save active: no manual save button (persistence is invisible). */}



            {input.role === "propietario" ? (
              result.semaforo === "Verde" ? (
                <Card className="p-5 bg-green-50 border-green-300">
                  <h3 className="font-bold text-green-900">{t("locationDiagnosis", "ownerGreenTitle")}</h3>
                  <p className="text-sm text-green-900/80 mt-1 mb-3">{t("locationDiagnosis", "ownerGreenMsg")}</p>
                  <Button onClick={() => navigate("/dashboard")} className="w-full sm:w-auto bg-yellow-400 text-black hover:bg-yellow-500">
                    {t("locationDiagnosis", "ctaGreenBtn")}
                  </Button>
                </Card>
              ) : result.semaforo === "Amarillo" ? (
                <Card className="p-5 bg-yellow-50 border-yellow-300">
                  <h3 className="font-bold text-yellow-900">{t("locationDiagnosis", "ownerYellowTitle")}</h3>
                  <p className="text-sm text-yellow-900/80 mt-1 mb-3">{t("locationDiagnosis", "ownerYellowMsg")}</p>
                  <Button onClick={onBookSession} className="w-full sm:w-auto bg-black text-white hover:bg-black/90">
                    {t("locationDiagnosis", "ctaRedBtn")}
                  </Button>
                </Card>
              ) : (
                <Card className="p-5 bg-red-50 border-red-300">
                  <h3 className="font-bold text-red-900">{t("locationDiagnosis", "ownerRedTitle")}</h3>
                  <p className="text-sm text-red-900/80 mt-1 mb-3">{t("locationDiagnosis", "ownerRedMsg")}</p>
                  <Button onClick={onBookSession} className="w-full sm:w-auto bg-black text-white hover:bg-black/90">
                    {t("locationDiagnosis", "ctaRedBtn")}
                  </Button>
                </Card>
              )
            ) : null}

            {/* Bridge to Calculadora */}
            <Card className="p-5 border-l-4 border-l-yellow-400">
              <h3 className="font-semibold">{t("locationDiagnosis", "transitionTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-3">{t("locationDiagnosis", "transitionSub")}</p>
              <Button
                variant="outline"
                onClick={() => navigate(lang === "es" ? "/herramientas/calculadora" : "/en/tools/calculator")}
              >
                <CalcIcon className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {t("locationDiagnosis", "transitionBtn")}
              </Button>
            </Card>
          </div>
        )}
        </div>
      </main>

      {/* Sticky paywall — public only */}
      {locked && !isPrivate && (
        <div className="fixed bottom-0 inset-x-0 z-50 border-t bg-background/95 backdrop-blur shadow-[0_-10px_30px_rgba(0,0,0,0.08)]">
          <div className="container max-w-4xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{t("locationDiagnosis", "paywallTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("locationDiagnosis", "paywallSubtitle")}</p>
            </div>
            <Button onClick={onSignupClick} className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-black font-semibold min-h-11">
              {t("locationDiagnosis", "paywallCta")}
            </Button>
          </div>
        </div>
      )}

      {!isPrivate && !hideChrome && <CaptureFooter slug="diagnostico" pathEs="/diagnostico-locales-comerciales" pathEn="/en/tools/my-premises" shareText={t("shareLanding", "diagText")} />}
      {children}
    </div>
  );
};

// ── Freemium portfolio limit modal (private mode) ──
const FreemiumLimitModal = ({
  open, onOpenChange, onCta,
}: { open: boolean; onOpenChange: (v: boolean) => void; onCta: () => void }) => {
  const { t } = useLanguage();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
      onClick={() => onOpenChange(false)}
    >
      <Card className="max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
        <Sparkles className="h-10 w-10 text-foreground mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="text-lg font-bold mb-2">{t("locationDiagnosis", "freemiumLimitTitle")}</h3>
        <p className="text-sm text-muted-foreground mb-5">
          {t("locationDiagnosis", "freemiumLimitMsg")}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("locationDiagnosis", "historyNew")}
          </Button>
          <Button onClick={onCta} className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold">
            {t("locationDiagnosis", "freemiumLimitCta")} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default DiagnosticadorLocales;
